import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFileStatusQuery } from '@omnea-app/fe-documents/useFileStatusQuery'
import { ImageLightbox } from '@omnea-app/omui/ImageLightbox'

type Props = {
  filestoreId: string
  alt?: string
}

// Renders an inline comment image referenced by filestoreId: resolves it to a
// (short-lived) signed URL — polling while the upload is still being virus
// scanned — then shows a capped thumbnail that opens a full-size lightbox.
export const CommentImage = ({ filestoreId, alt }: Props) => {
  const { t } = useTranslation('requests')
  const [isExpanded, setIsExpanded] = useState(false)
  const { signedUrl, validationStatus, isTimeout } = useFileStatusQuery({
    filestoreId,
    enabled: true,
    // Cache aggressively - no need to re-query the same image
    gcTime: 600_000,
    staleTime: 600_000,
  })

  if (!signedUrl) {
    const isUnavailable = isTimeout || validationStatus === 'QUARANTINED' || validationStatus === 'NOT_FOUND'
    if (isUnavailable) {
      return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.disabled', py: 1 }}>
          <BrokenImageOutlinedIcon fontSize="small" />
          <Typography variant="caption">{t('request-comments.images.unavailable')}</Typography>
        </Stack>
      )
    }
    return <Skeleton variant="rounded" width={300} height={200} sx={{ maxWidth: '100%' }} />
  }

  return (
    <>
      <ButtonBase
        onClick={() => setIsExpanded(true)}
        aria-label={t('request-comments.images.expand')}
        sx={{ borderRadius: 2, verticalAlign: 'bottom' }}
      >
        <Box
          component="img"
          src={signedUrl}
          alt={alt ?? ''}
          sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 2, objectFit: 'contain' }}
        />
      </ButtonBase>
      <ImageLightbox
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        closeLabel={t('request-comments.images.close-preview')}
      >
        <Box component="img" src={signedUrl} alt={alt ?? ''} sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
      </ImageLightbox>
    </>
  )
}
